import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, GuardResult, Router, RouterStateSnapshot } from '@angular/router';
import { AppRoutingService } from '@core/app-routing.service';
import { SpinnerService } from '@core/spinner.service';
import { GuardHelpers } from '@guards/guard-helpers';
import { AccessControlService } from '@services/access-control/access-control.service';
import { Observable, of } from 'rxjs';
import { map, skipWhile } from 'rxjs/operators';
import { AppConfigService } from '../appconfig.service';
import { AuthenticationService } from '../authentication.service';
import { UserService } from '../user.service';

/**
 * authenticationGuard ensures the user is authenticated before a route can be accessed.
 * Unauthenticated users will be redirected to the login
 */
export const authenticationGuard = GuardHelpers.wrapWithLogs(
  'authenticationGuard',
  (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<GuardResult> => {
    // injected services
    const accessService = inject(AccessControlService);
    const authService = inject(AuthenticationService);
    const userService = inject(UserService);
    const appConfigService = inject(AppConfigService);
    const appRoutingService = inject(AppRoutingService);
    const spinnerService = inject(SpinnerService);
    const router = inject(Router);

    const { url } = state;

    spinnerService.showAppWideSpinner$.next(true);

    // if the route does not require authentication proceed
    if (accessService.isUnauthorizedRoute(url)) {
      spinnerService.showAppWideSpinner$.next(false);

      return of(true);
    }
    return authService.authStatus
      .pipe(
        skipWhile(status => status === null),
        map((status) => {
          // when the user is logged in
          if (status) {
            // verify the user has agreed to the EULA
            if (!userService.hasAgreed && url !== '/agreement') {
              return router.parseUrl('/agreement');
            }

            spinnerService.showAppWideSpinner$.next(false);

            return true;
          }
          // User is NOT actively logged in
          // Store the attempted URL for redirect later
          appConfigService.redirectUrl = url;

          // Navigate to the login page.
          const loginRouteUrl = appRoutingService.findLoginRoute().toString();

          spinnerService.showAppWideSpinner$.next(false);

          return router.parseUrl(loginRouteUrl);
        })
      );
  }
) satisfies CanActivateFn;
